import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, Crown, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "./components/Footer";
import { formatHUF } from "../lib/format";
import { useFanClub, useSettings } from "../contexts/AppContext";

const DiscordIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

export default function ProfilomPage() {
  const { member, tiers, getProfile, createUpgradeCheckout, getDiscordConnectUrl } = useFanClub();
  const { social_discord } = useSettings();
  const [upgradeLoading, setUpgradeLoading] = useState(null);
  const [discordLoading, setDiscordLoading] = useState(false);

  useEffect(() => {
    getProfile();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const discord = params.get('discord');
    if (discord === 'connected') {
      toast.success('Discord összekapcsolva! A tagsági rangod kiosztásra került.');
      getProfile();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (discord === 'error') {
      const reason = params.get('reason');
      const messages = {
        expired: 'A Discord csatlakozás lejárt. Próbáld újra.',
        token: 'Discord token hiba (hibás code vagy redirect_uri). Nézd a backend storage/logs/laravel.log fájlt.',
        user: 'Discord felhasználó lekérés sikertelen. Nézd a backend storage/logs/laravel.log fájlt.',
        guild: 'Szerverre csatlakozás vagy rang kiosztás sikertelen. A bot a szerveren van? Nézd a backend storage/logs/laravel.log fájlt.',
        config: 'Discord beállítás hiányzik (.env: DISCORD_CLIENT_ID, SECRET, BOT_TOKEN, GUILD_ID).',
        member: 'Tag nem található.',
        missing: 'Hiányzó code vagy state a Discord válaszból.',
      };
      const msg = messages[reason] || `Discord csatlakozás sikertelen (${reason || 'ismeretlen'}). Nézd a backend storage/logs/laravel.log fájlt.`;
      toast.error(msg);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [getProfile]);

  if (!member) return null;

  const benefits = member.tier?.benefits;
  const benefitsList = Array.isArray(benefits)
    ? benefits.filter(Boolean)
    : (typeof benefits === 'string' ? benefits.split(',').map((b) => b.trim()).filter(Boolean) : []);

  const currentTierId = member.tier?.id;
  const currentOrder = member.tier?.sort_order != null
    ? Number(member.tier.sort_order)
    : (tiers.find((t) => String(t.id) === String(currentTierId))?.sort_order ?? 0);
  const higherTiers = tiers
    .filter((t) => (Number(t.sort_order) || 0) > currentOrder)
    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));

  return (
    <div data-testid="profilom-page">
      <div className="min-h-screen pt-24 sm:pt-32 pb-32 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8 sm:mb-16 animate-fade-in">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-primary mb-2 sm:mb-4">Fan Club</p>
            <h1 className="text-3xl sm:text-5xl font-bold font-['Syne'] tracking-tighter">Profilom</h1>
          </div>
          <div className="glass rounded-xl p-6 sm:p-8 animate-fade-in space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-['Syne']">{member.name}</h2>
                <p className="text-sm text-muted-foreground">{member.email}</p>
              </div>
            </div>
            {member.tier && (
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center gap-3">
                  <Crown className="h-5 w-5 shrink-0" style={{ color: member.tier.color || 'var(--primary)' }} />
                  <div>
                    <p className="text-xs text-muted-foreground">Tag szint</p>
                    <p className="font-semibold" style={{ color: member.tier.color || 'var(--primary)' }}>{member.tier.name}</p>
                  </div>
                </div>
                {member.tier_expires_at && (
                  <div className="text-sm">
                    <p className="text-xs text-muted-foreground">Lejár</p>
                    <p className="text-muted-foreground">{new Date(member.tier_expires_at).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                )}
                {social_discord && (
                  <div className="ml-auto flex items-center gap-2">
                    {member.discord_user_id && (
                      <span className="text-xs text-muted-foreground">Discord összekapcsolva</span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 rounded-full border-[#5865F2] text-[#5865F2] hover:bg-[#5865F2]/20 hover:text-[#5865F2]"
                      disabled={discordLoading}
                      onClick={async () => {
                        setDiscordLoading(true);
                        try {
                          const url = await getDiscordConnectUrl();
                          if (url) window.location.href = url;
                          else toast.error('Discord csatlakozás nem érhető el.');
                        } catch (e) {
                          const msg = e.response?.data?.error || e.message || 'Discord csatlakozás sikertelen.';
                          const status = e.response?.status;
                          if (status) console.error('Discord connect API hiba:', status, e.response?.data);
                          else console.error('Discord connect hiba (nincs válasz):', e);
                          toast.error(msg);
                        } finally {
                          setDiscordLoading(false);
                        }
                      }}
                    >
                      <DiscordIcon />
                      {member.discord_user_id ? 'Discord újracsatlakozás' : 'Csatlakozás a Discordhoz'}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {member.tier && benefitsList.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">Tagság előnyei</h3>
                <ul className="grid gap-2">
                  {benefitsList.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      <span className="text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {higherTiers.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10 p-4 sm:p-5 bg-gradient-to-r from-primary/20 to-primary/5 rounded-xl">
                <h3 className="text-sm font-semibold mb-2">Feljebb váltás</h3>
                <p className="text-xs text-muted-foreground mb-4">Válts magasabb szintre több előnyért.</p>
                <div className="grid gap-3">
                  {higherTiers.map((tier) => (
                    <div key={tier.id} className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: (tier.color || '#888') + '30' }}>
                          <Crown className="h-5 w-5" style={{ color: tier.color }} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{tier.name}</p>
                          <p className="text-xs text-muted-foreground">{tier.price === 0 ? 'Ingyenes' : formatHUF(tier.price) + '/hó'}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="rounded-full bg-primary hover:bg-primary/90 shrink-0"
                        disabled={upgradeLoading === tier.id}
                        onClick={async () => {
                          setUpgradeLoading(tier.id);
                          try {
                            const res = await createUpgradeCheckout(tier.id);
                            if (res.is_free) {
                              toast.success('Frissítés sikeres!');
                              getProfile();
                            } else if (res.checkout_url) {
                              window.location.href = res.checkout_url;
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.message || err.response?.data?.errors?.tier_id?.[0] || 'Hiba történt');
                          } finally {
                            setUpgradeLoading(null);
                          }
                        }}
                      >
                        {upgradeLoading === tier.id ? 'Feldolgozás...' : 'Bővítés'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
